import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {PaginationInterface} from '../../interfaces/responses/pagination.interface';
import {ProductInterfaces} from '../../interfaces/requests/product.interfaces';
import {
  Product,
  ProductQueryParams,
  ProductResponseInterfaces,
} from '../../interfaces/responses/product-response.interfaces';
import {toHttpParams} from '../../utils/app.util';

const env = environment;

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private http = inject(HttpClient);

  getPaginatedProducts(
    queryParams: ProductQueryParams,
  ): Observable<PaginationInterface<Product[]>> {
    let token: string | null = null;

    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products`;

    return this.http
      .get<PaginationInterface<Product[]>>(url, {
        params: toHttpParams(queryParams),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(
            () =>
              new Error(`Error fetching products: ${error.message || error}`)
          );
        })
      );
  }

  getProductById(id: string): Observable<Product> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products/${id}`;
    return this.http
      .get<Product>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(`Error fetching product ${error}`));
        })
      );
  }

  createProduct(
    data: ProductInterfaces
  ): Observable<ProductResponseInterfaces> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products`;
    return this.http
      .post<ProductResponseInterfaces>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(`Error creating product ${error}`));
        })
      );
  }

  updateProduct(
    id: string,
    data: ProductInterfaces
  ): Observable<ProductResponseInterfaces> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products/${id}`;
    return this.http
      .put<ProductResponseInterfaces>(url, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  }

  deleteProduct(id: string): Observable<ProductResponseInterfaces> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products/${id}`;
    return this.http
      .delete<ProductResponseInterfaces>(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(`Error deleting product ${error}`));
        })
      );
  }

  toggleStatus(id: string): Observable<Product> {
    let token: string | null = null;
    if (typeof window !== 'undefined') {
      token = localStorage.getItem('tokenn');
    }
    const url = `${env.apiUrl}/products/${id}/status`;
    return this.http
      .patch<Product>(url, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(`Error toggling product status ${error}`));
        })
      );
  }
}
